import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  AlertTriangle, Clock, CheckCheck, FileWarning, Hourglass, Ban, Receipt, ListChecks, FileClock, MessageSquare, CalendarClock,
  PiggyBank, StickyNote, Send, UserCheck, Timer, ShieldCheck, Scale, RefreshCw,
} from 'lucide-react';
import { Button, Banner, Empty, ErrorBlock, Skeleton, tone } from '../kit';
import { CoordinatorPicker, LinkBtn, MoreToggle, fileLink, viewLink, plural } from './shared';

// Oversight: one prioritised list of what needs someone, across the caseload. Groups come from
// GET /sc/insights/oversight in priority order (money at risk, compliance, caseload, team); here the
// groups holding anything late or blocked move to the top. Each item has one action that goes
// straight to where it is fixed. Zero groups collapse into one "All clear" line.

const SHOW = 5;
const ICON = {
  claims_held: FileWarning, claim_age: Hourglass, rejected: Ban, invoices: Receipt, tasks: ListChecks, reports: FileClock,
  complaints: MessageSquare, plans: CalendarClock, budgets: PiggyBank, notes: StickyNote, referrals: Send,
  hours_approval: UserCheck, hours_unsubmitted: Timer, consents: ShieldCheck, conflicts: Scale,
};
const hoursLink = (it) => viewLink(`view=hours&hours_tab=team${it.period_start ? `&period=${it.period_start}` : ''}`);
// The one action for each kind of item, and where it goes.
const ACTION = {
  claims_held: (it) => ((it.codes || []).includes('checklist') && it.coordination_id
    ? { label: 'Finish setup', to: fileLink(it.coordination_id, 'setup=1') }
    : { label: 'Open Fix list', to: viewLink('view=claims') }),
  claim_age: () => ({ label: 'Claim now', to: viewLink('view=claims') }),
  rejected: () => ({ label: 'Fix or write off', to: viewLink('view=claims&claims_tab=lines') }),
  invoices: (it) => ({ label: 'Follow up', to: viewLink(`view=claims&claims_tab=invoices${it.invoice_id ? `&invoice=${it.invoice_id}` : ''}`) }),
  tasks: (it) => ({ label: 'Open tasks', to: fileLink(it.coordination_id) }),
  reports: (it) => ({ label: 'Open report', to: fileLink(it.coordination_id, 'tab=documents') }),
  complaints: (it) => ({ label: 'Open complaint', to: fileLink(it.coordination_id, 'tab=compliance') }),
  plans: (it) => ({ label: 'Update plan', to: fileLink(it.coordination_id, 'tab=plan') }),
  budgets: (it) => ({ label: 'Open budget', to: fileLink(it.coordination_id, 'tab=plan') }),
  notes: (it) => ({ label: 'Open file', to: fileLink(it.coordination_id) }),
  referrals: (it) => ({ label: 'Follow up', to: viewLink(`view=referrals${it.referral_id ? `&referral=${it.referral_id}` : ''}`) }),
  hours_approval: (it) => ({ label: 'Review hours', to: hoursLink(it) }),
  hours_unsubmitted: (it) => ({ label: 'See hours', to: hoursLink(it) }),
  consents: (it) => ({ label: 'Renew consent', to: fileLink(it.coordination_id, 'tab=compliance') }),
  conflicts: (it) => ({ label: 'Manage conflict', to: fileLink(it.coordination_id, 'tab=compliance') }),
};
// Items about a person in the team (hours) are titled with the coordinator, not a participant.
const TEAM_GROUPS = ['hours_approval', 'hours_unsubmitted'];

export default function OversightTab({ load, coordinator, onCoordinator }) {
  const { data, loading, error, reload } = load;
  const groupRefs = useRef({});
  const groups = useMemo(() => {
    const list = (data && data.groups) || [];
    // Late or blocked first, then the rest, each keeping the server's priority order.
    return [...list.filter((g) => g.count && g.red), ...list.filter((g) => g.count && !g.red)];
  }, [data]);
  const clear = ((data && data.groups) || []).filter((g) => !g.count);
  const jump = (key) => {
    const el = groupRefs.current[key];
    if (!el) return;
    if (el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const h = el.querySelector('h2');
    if (h) h.focus({ preventScroll: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <CoordinatorPicker value={coordinator} onChange={onCoordinator} coordinators={data && data.coordinators} />
        <Button variant="ghost" size="sm" icon={RefreshCw} onClick={reload} loading={loading && !!data} className="ml-auto">Refresh</Button>
      </div>

      {error && !data ? <ErrorBlock error={error} onRetry={reload} />
        : !data ? <OversightSkeleton />
          : (
            <>
              <Summary data={data} groups={groups} onJump={jump} coordinatorName={coordinator ? ((data.coordinators || []).find((c) => c.id === coordinator) || {}).name : null} />
              {data.partial && data.partial.length > 0 && (
                <Banner tone="amber" title={`Couldn’t check ${data.partial.join(', ')}`}
                  action={<Button size="sm" variant="secondary" onClick={reload}>Try again</Button>}>
                  The list may be missing those items until they load.
                </Banner>
              )}
              {!groups.length ? (
                <div className="bg-white rounded-2xl border border-slate-200/80">
                  <Empty icon={CheckCheck} title="Nothing needs attention">
                    {coordinator ? 'Nothing is late or due for this coordinator’s participants, hours or claims.' : 'Notes, plans, budgets, compliance, referrals, hours and claims are all up to date. New items show here as they fall due.'}
                  </Empty>
                </div>
              ) : groups.map((g) => (
                <div key={g.key} ref={(el) => { groupRefs.current[g.key] = el; }} className="scroll-mt-4">
                  <GroupPanel g={g} />
                </div>
              ))}
              {clear.length > 0 && groups.length > 0 && (
                <p className="flex items-start gap-2 text-[13px] text-slate-600 px-1">
                  <CheckCheck size={15} className="text-emerald-600 mt-0.5 flex-shrink-0" aria-hidden />
                  <span><span className="font-semibold text-slate-800">All clear:</span> {clear.map((g) => g.title).join(' · ')}</span>
                </p>
              )}
            </>
          )}
    </div>
  );
}

function Summary({ data, groups, onJump, coordinatorName }) {
  if (!data.total) return null;
  return (
    <section aria-label="Summary" className="bg-white rounded-2xl border border-slate-200/80 px-4 sm:px-5 py-3.5">
      <p className="text-[14px] text-slate-800">
        <span className="font-semibold">{plural(data.total, 'item needs', 'items need')} attention</span>
        {data.red > 0 && <span className="text-rose-700 font-semibold"> · {plural(data.red, 'is', 'are')} late or blocked</span>}
        {coordinatorName && <span className="text-slate-500"> · {coordinatorName}</span>}
      </p>
      {/* Phones: one row that scrolls sideways inside the strip, so the groups start above the fold. */}
      <ul className="mt-2.5 flex gap-2 overflow-x-auto sm:flex-wrap sm:overflow-visible -mx-4 px-4 sm:mx-0 sm:px-0 pb-1 sm:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Jump to a group">
        {groups.map((g) => (
          <li key={g.key} className="flex-shrink-0">
            <button type="button" onClick={() => onJump(g.key)}
              className={clsx('inline-flex items-center gap-2 h-11 sm:h-8 pl-3 pr-2 rounded-full ring-1 ring-inset text-[12.5px] font-semibold transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                tone(g.red ? 'red' : 'amber').pill)}>
              {g.title}
              <span className="tabular-nums rounded-full bg-white/80 px-1.5 py-px text-[12px] font-bold">{g.count}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function GroupPanel({ g }) {
  const [all, setAll] = useState(false);
  const Icon = ICON[g.key] || AlertTriangle;
  const c = tone(g.red ? 'red' : 'amber');
  const items = all ? g.items : g.items.slice(0, SHOW);
  const headingId = `ov-h-${g.key}`;
  return (
    <section aria-labelledby={headingId} className="bg-white rounded-2xl border border-slate-200/80">
      <div className="flex items-start gap-3 px-4 sm:px-5 pt-4 pb-3">
        <span className={clsx('mt-0.5 w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0', c.icon)}><Icon size={16} aria-hidden /></span>
        <div className="min-w-0 flex-1">
          <h2 id={headingId} tabIndex={-1} className="text-[15px] font-semibold text-slate-900 leading-6 outline-none">
            {g.title}
            <span className={clsx('ml-2 align-middle text-[11px] tabular-nums font-bold px-1.5 py-px rounded-full', g.red ? 'bg-rose-600 text-white' : 'bg-slate-200/80 text-slate-700')}>{g.count}</span>
          </h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">
            {g.sub}
            {g.red > 0 && g.red < g.count && <span className="text-rose-700 font-semibold"> {plural(g.red, 'is', 'are')} late or blocked.</span>}
          </p>
        </div>
      </div>
      <ul className="divide-y divide-slate-100 border-t border-slate-100">
        {items.map((it) => <ItemRow key={it.id} g={g} it={it} />)}
      </ul>
      {(g.count > SHOW) && (
        <div className="px-4 sm:px-5 py-2 border-t border-slate-100 flex flex-wrap items-center gap-x-3">
          <MoreToggle shown={SHOW} total={g.items.length} open={all} onToggle={() => setAll((v) => !v)} />
          {g.count > g.items.length && all && <span className="text-[12px] text-slate-500">Showing the first {g.items.length} of {g.count}.</span>}
        </div>
      )}
    </section>
  );
}

function ItemRow({ g, it }) {
  const action = (ACTION[g.key] || (() => null))(it);
  const red = it.tone === 'red';
  const titleLink = !TEAM_GROUPS.includes(g.key) && g.key !== 'referrals' ? fileLink(it.coordination_id) : null;
  const DueIcon = red ? AlertTriangle : Clock;
  return (
    <li className="px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
      <div className="flex-1 min-w-0">
        <p className="text-[13.5px] font-semibold text-slate-900 leading-5 break-words">
          {titleLink
            ? <Link to={titleLink} className="rounded hover:text-indigo-700 hover:underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">{it.title}</Link>
            : it.title}
        </p>
        {it.detail && <p className="mt-0.5 text-[12.5px] text-slate-600 leading-5 line-clamp-2 break-words">{it.detail}</p>}
        <p className="mt-0.5 text-[12.5px] leading-5 flex flex-wrap items-center gap-x-2">
          {it.due && (
            <span className={clsx('inline-flex items-center gap-1 font-semibold', red ? 'text-rose-700' : 'text-amber-800')}>
              <DueIcon size={13} aria-hidden />{it.due}
            </span>
          )}
          {!TEAM_GROUPS.includes(g.key) && (
            <span className="text-slate-500">{g.key === 'referrals' ? `Assigned to ${it.coordinator || 'nobody'}` : it.coordinator ? `Coordinator ${it.coordinator}` : 'No coordinator assigned'}</span>
          )}
        </p>
      </div>
      {action && action.to && (
        <div className="flex-shrink-0">
          <LinkBtn to={action.to} variant="secondary" aria-label={`${action.label}: ${it.title}`}>{action.label}</LinkBtn>
        </div>
      )}
    </li>
  );
}

function OversightSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading">
      <Skeleton className="h-20 rounded-2xl" />
      {[0, 1].map((i) => (
        <div key={i} className="bg-white rounded-2xl border border-slate-200/80 p-5 space-y-3">
          <div className="flex items-center gap-3"><Skeleton className="w-8 h-8 rounded-xl" /><Skeleton className="h-4 w-48" /></div>
          {[0, 1, 2].map((j) => <Skeleton key={j} className="h-10" />)}
        </div>
      ))}
    </div>
  );
}

